import { resourceHasSuggestedMatchTag, toUiReviewStatus } from './lms-course-review.utils';

/**
 * Transform API suggestion to UI resource option.
 * @param {Object} topSuggestion - API top suggestion object
 * @returns {Object|null}
 */
export const toSuggestionResource = (topSuggestion) => {
  if (!topSuggestion?.resourceKey) return null;
  if (!resourceHasSuggestedMatchTag(topSuggestion)) return null;

  const { resourceKey, resourceName, description, score, tags, metadata } = topSuggestion;
  const scoreLabel =
    score != null ? `Match score: ${(score * 100).toFixed(0)}%` : undefined;

  return {
    id: resourceKey,
    resourceKey,
    score,
    name: resourceName?.trim() || `Course #${resourceKey}`,
    description: description?.trim() || scoreLabel,
    tags: tags ?? metadata?.tags,
  };
};

/**
 * Transform API catalog row to UI queue item.
 * @param {Object} catalogRow - API catalog row
 * @returns {Object}
 */
export const toQueueItem = (catalogRow) => ({
  id: catalogRow.id,
  canvasCourseName: catalogRow.title,
  canvasCourseId: catalogRow.lmsResourceId,
  queuedAt: catalogRow.createdAt,
  reviewStatus: catalogRow.reviewStatus,
  status: toUiReviewStatus(catalogRow.reviewStatus),
  suggestedResource: toSuggestionResource(catalogRow.topSuggestion),
  linkedResourceId: catalogRow.resourceKey ?? undefined,
  linkedResourceName: undefined,
  resolvedAt: catalogRow.reviewedAt ?? undefined,
  lmsCode: catalogRow.lmsCode,
  description: catalogRow.description,
});

/**
 * Transform API list response to hook contract.
 * @param {Object} apiResponse - API response
 * @returns {Object}
 */
export const toQueueListResponse = (apiResponse) => ({
  items: (apiResponse?.data || []).map(toQueueItem),
  totalCount: apiResponse?.total ?? 0,
  limit: apiResponse?.limit,
  offset: apiResponse?.offset,
});

/**
 * Transform search hit to resource option.
 * @param {Object} searchHit - API search hit
 * @returns {Object}
 */
export const toResourceOption = (searchHit) => ({
  id: searchHit.resourceKey,
  resourceKey: searchHit.resourceKey,
  name: searchHit.resourceName,
  label: searchHit.resourceName,
  description: searchHit.description,
  similarityScore: searchHit.score,
  tags: searchHit.tags ?? searchHit.metadata?.tags,
  metadata: searchHit.metadata,
});

/**
 * Transform array of search hits to resource options.
 * @param {Array} searchHits - API search hits
 * @returns {Array}
 */
export const toResourceOptions = (searchHits) =>
  (Array.isArray(searchHits) ? searchHits : [])
    .filter(resourceHasSuggestedMatchTag)
    .map(toResourceOption);

/**
 * Enrich suggestion with data from search results.
 * @param {Object} suggestion - Base suggestion
 * @param {Array} searchHits - Search results
 * @returns {Object}
 */
export const enrichSuggestionFromSearch = (suggestion, searchHits) => {
  if (!suggestion?.resourceKey || !searchHits?.length) return suggestion;

  const match = searchHits.find((hit) => hit.resourceKey === suggestion.resourceKey);
  if (!match) return suggestion;

  return {
    ...suggestion,
    id: match.resourceKey,
    name: match.resourceName || suggestion.name,
    description: match.description || suggestion.description,
    score: suggestion.score ?? match.score ?? match.similarityScore,
  };
};

/**
 * Build resolve request body.
 * @param {Object} options
 * @param {string|number} options.resourceKey - Resource key to link
 * @returns {Object}
 */
export const toResolveBody = ({ resourceKey }) => ({
  resourceKey,
});

/**
 * Build bulk-resolve request body from queue items with suggestions.
 * @param {Array<{ id: number|string, suggestedResource: { resourceKey: number|string } }>} items
 * @returns {{ items: Array<{ catalogId: number|string, resourceKey: number|string }> }}
 */
export const toBulkResolveBody = (items) => ({
  items: items.map((item) => ({
    catalogId: item.id,
    resourceKey: item.suggestedResource.resourceKey,
  })),
});

/**
 * Build create-and-resolve request body.
 * @param {Object} options
 * @param {string} options.name - Resource name
 * @param {string} [options.externalId] - External ID
 * @param {string} [options.description] - Description
 * @returns {Object}
 */
export const toCreateAndResolveBody = ({ name, externalId, description }) => {
  const body = {};
  const trimmedName = name?.trim();
  const trimmedExternalId = externalId?.trim();
  const trimmedDescription = description?.trim();

  if (trimmedName) body.resourceName = trimmedName;
  if (trimmedDescription) body.description = trimmedDescription;
  if (trimmedExternalId) body.externalId = trimmedExternalId;

  return body;
};
